"use client";

import { useMemo, useState } from "react";
import { ChevronDown } from "@/assets/icons/mdi-chevron-down";
import { BannerSection } from "@/components/sections/BannerSection";
import { BreadcrumbHomepage } from "@/components/sections/BreadcrumbHomepage";

type LegalContentBlock = {
  mainText?: string;
  bulletPoints?: string[];
  additionalText?: string[];
  contactInfo?: string;
};

type LegalArticle = {
  title: string;
  content: LegalContentBlock[];
};

interface LegalDocumentsPageProps {
  bannerTitle: string;
  eyebrow: string;
  intro: string;
  articles: LegalArticle[];
}

export const LegalDocumentsPage = ({
  bannerTitle,
  eyebrow,
  intro,
  articles,
}: LegalDocumentsPageProps) => {
  const [openArticles, setOpenArticles] = useState<number[]>(
    Array.from({ length: articles.length }, (_, index) => index),
  );

  const gradientClass = useMemo(
    () =>
      "bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.55)_0%,_rgba(255,255,255,0)_55%),linear-gradient(180deg,_#fef5f5_0%,_#fde8e8_45%,_#f9dfdf_100%)]",
    [],
  );

  const toggleArticle = (index: number) => {
    setOpenArticles((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const renderContent = (content: LegalContentBlock[]) => {
    return (
      <div className="space-y-5 text-black/85">
        {content.map((section, idx) => (
          <div key={idx} className="space-y-3">
            {section.mainText && (
              <p className="leading-relaxed">{section.mainText}</p>
            )}

            {section.bulletPoints && (
              <ul className="space-y-2">
                {section.bulletPoints.map((point, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 leading-relaxed"
                  >
                    <span className="mt-[0.4rem] h-1.5 w-1.5 rounded-full bg-primary" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.additionalText?.map((text, i) => (
              <p key={i} className="leading-relaxed text-black/80">
                {text}
              </p>
            ))}

            {section.contactInfo && (
              <p className="rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 font-semibold text-black">
                {section.contactInfo}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <section>
      <BannerSection title={bannerTitle} />
      <BreadcrumbHomepage className="bg-secondary text-black" />

      <div
        className={`relative isolate overflow-hidden px-4 py-14 text-black ph:px-24 md:px-48 lg:px-72 xl:px-96 ${gradientClass}`}
      >
        <div className="pointer-events-none absolute -top-16 right-4 h-48 w-48 rounded-full bg-primary/15 blur-3xl md:h-64 md:w-64" />
        <div className="pointer-events-none absolute -bottom-16 left-4 h-40 w-40 rounded-full bg-[#c27b7b]/20 blur-3xl md:h-56 md:w-56" />

        <div className="relative mx-auto max-w-6xl space-y-6">
          <div className="rounded-3xl border border-black/10 bg-white/70 p-6 shadow-[0_18px_45px_rgba(105,43,43,0.08)] backdrop-blur-sm md:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary/80">
              {eyebrow}
            </p>
            <p className="mt-3 text-base leading-relaxed text-black/80 md:text-lg">
              {intro}
            </p>
          </div>

          <div className="space-y-4">
            {articles.map((article, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-3xl border border-black/10 bg-white/75 shadow-[0_14px_35px_rgba(0,0,0,0.06)] backdrop-blur-sm"
              >
                <button
                  onClick={() => toggleArticle(index)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-primary/15 px-2 text-xs font-semibold text-primary">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h2 className="text-lg font-semibold text-black md:text-xl">
                      {article.title}
                    </h2>
                  </div>

                  <ChevronDown
                    className={`shrink-0 transform transition-transform duration-200 ${
                      !openArticles.includes(index) ? "rotate-90" : ""
                    }`}
                    width={34}
                  />
                </button>

                <div
                  className={`overflow-hidden px-5 transition-all duration-300 md:px-6 ${
                    openArticles.includes(index)
                      ? "pb-6 opacity-100"
                      : "max-h-0 pb-0 opacity-0"
                  }`}
                >
                  {renderContent(article.content)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
